import { useEffect, useRef, useState, ReactNode } from "react";
import AuthContext, { type AppSession } from "./AuthContext";
import { signInWithEmailPassword } from "./utils/signIn";
import { signUpWithEmailPassword } from "./utils/signUp";
import { signOut as performSignOut } from "./utils/signOut";
import { resetUserPassword, updateUserPassword } from "./utils/passwordManagement";
import { updateUserProfile } from "./utils/profileManagement";
import { signInWithGoogle } from "./utils/socialAuth";
import { verifyAdmin } from "@/helpers/verifyAdmin";
import { queryClient } from "@/lib/react-query";
import { clearBrowserAuthState, clearTransientBrowserState } from "@/utils/authStorage";
import { debugLogger } from '@/utils/debug';
import { fetchAuthSession } from '@/services/authSession';
import { AUTH_UNAUTHORIZED_EVENT } from '@/services/platformApi/client';
import type { AppUser } from '@/types/supabase';

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<AppUser | null>(null);
  const [session, setSession] = useState<AppSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isAdminLoading, setIsAdminLoading] = useState(false);
  const previousUserIdRef = useRef<string | null>(null);
  const sessionRequestIdRef = useRef(0);
  const signingOutRef = useRef(false);

  const checkAdminStatus = async (userId: string) => {
    setIsAdminLoading(true);

    try {
      const adminStatus = await verifyAdmin(userId);
      setIsAdmin(adminStatus);
      debugLogger.log("Admin status updated:", adminStatus);
    } catch (error) {
      console.error("Error checking admin status:", error);
      setIsAdmin(false);
    } finally {
      setIsAdminLoading(false);
    }
  };

  const syncAuthState = (nextUser: AppUser | null) => {
    const nextUserId = nextUser?.id ?? null;
    const previousUserId = previousUserIdRef.current;

    if (previousUserId !== nextUserId) {
      queryClient.clear();
      clearTransientBrowserState();
      previousUserIdRef.current = nextUserId;
      debugLogger.log("Cleared React Query cache due to auth user change", {
        previousUserId,
        nextUserId,
      });
    }

    setSession(nextUser ? { user: nextUser } : null);

    if (nextUser) {
      setUser(nextUser);
      void checkAdminStatus(nextUser.id);
      return;
    }

    setUser(null);
    setIsAdmin(false);
    setIsAdminLoading(false);
  };

  const loadSession = async () => {
    if (signingOutRef.current) {
      return;
    }

    const requestId = ++sessionRequestIdRef.current;

    try {
      const { user: nextUser } = await fetchAuthSession();

      if (signingOutRef.current || requestId !== sessionRequestIdRef.current) {
        return;
      }

      debugLogger.log("Loaded auth session:", nextUser?.email || "No session");
      syncAuthState(nextUser);
    } catch (error) {
      if (signingOutRef.current || requestId !== sessionRequestIdRef.current) {
        return;
      }

      console.error("Error loading auth session:", error);
      setUser(null);
      setSession(null);
      setIsAdmin(false);
      setIsAdminLoading(false);
      previousUserIdRef.current = null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadSession();

    const handleFocus = () => {
      void loadSession();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void loadSession();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    const handleUnauthorized = () => {
      sessionRequestIdRef.current += 1;
      clearBrowserAuthState();
      syncAuthState(null);
      setLoading(false);
    };

    window.addEventListener(AUTH_UNAUTHORIZED_EVENT, handleUnauthorized);

    return () => {
      window.removeEventListener(AUTH_UNAUTHORIZED_EVENT, handleUnauthorized);
    };
  }, []);

  // Wrapper functions to match the expected interface
  const signIn = async (email: string, password: string) => {
    const result = await signInWithEmailPassword(email, password);

    if (result.success) {
      await loadSession();
    }

    return result;
  };

  const signUp = async (email: string, password: string, firstName: string, lastName: string) => {
    const result = await signUpWithEmailPassword(email, password, firstName, lastName);

    if (result?.success) {
      await loadSession();
    }

    return result;
  };

  const resetPassword = async (email: string, redirectTo: string) => {
    return await resetUserPassword(email, redirectTo);
  };

  const updatePassword = async (newPassword: string) => {
    return await updateUserPassword(newPassword);
  };

  const updateProfile = async (data: { 
    first_name?: string; 
    last_name?: string; 
    email?: string | null;
    avatar_url?: string;
    whatsapp_number?: string;
  }) => {
    if (!user) throw new Error("Not authenticated");
    await updateUserProfile(user.id, data);
    await loadSession();
  };

  const toggleMfa = async (enable: boolean) => {
    // Placeholder implementation for MFA toggle
    debugLogger.log("MFA toggle not yet implemented:", enable);
  };

  const signOut = async () => {
    signingOutRef.current = true;
    sessionRequestIdRef.current += 1;
    syncAuthState(null);
    setLoading(false);
    try {
      await performSignOut();
    } finally {
      signingOutRef.current = false;
    }
  };

  const deleteAccount = async () => {
    // For now, we'll just sign out the user
    // In the future, this could be expanded to actually delete the account
    await signOut();
  };

  const value = {
    user,
    session,
    loading,
    isAdmin,
    isAdminLoading,
    refreshSession: loadSession,
    signIn,
    signUp,
    signOut,
    signInWithGoogle,
    updateProfile,
    toggleMfa,
    updatePassword,
    resetPassword,
    deleteAccount,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
